import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

const brandValues = [
  { icon: 'ri-shield-check-line', title: '正版合规', desc: '所有漫画内容均获得正版授权，尊重创作者权益。' },
  { icon: 'ri-flashlight-line', title: '高清流畅', desc: '高清画质配合下拉式阅读，带来极致流畅的阅读体验。' },
  { icon: 'ri-timer-line', title: '每日更新', desc: '热门连载作品保持同步更新，让追更不再等待。' },
  { icon: 'ri-device-line', title: '多端适配', desc: '完美支持电脑、平板和手机，随时随地畅享阅读。' },
];

const testimonials = [
  {
    name: '漫画迷小陈',
    role: '资深韩漫读者',
    content: '用了虚空韩漫网快一年了，画质是真的好，下拉式阅读特别方便。而且更新速度很快，追更体验一流。',
    rating: 5,
  },
  {
    name: '二次元阿琳',
    role: '韩漫爱好者',
    content: '最爱的韩漫平台！分类很全，想看的漫画基本都能找到。界面干净没有广告干扰，阅读体验满分。',
    rating: 5,
  },
  {
    name: '阅读达人老王',
    role: '漫画内容创作者',
    content: '作为同行我很认可虚空韩漫网的正版理念。正版合规对行业健康发展很重要，值得推荐给所有读者。',
    rating: 5,
  },
  {
    name: '学生党小林',
    role: '大学生读者',
    content: '免费的韩漫阅读平台真的很难得，而且用手机看特别方便。已经推荐给室友了，大家都在用。',
    rating: 5,
  },
  {
    name: '上班族小张',
    role: '通勤阅读用户',
    content: '每天通勤路上看一两话，下拉式阅读单手操作很方便。收藏功能也很实用，追更不会漏。',
    rating: 4,
  },
  {
    name: '漫评人阿杰',
    role: '韩漫评论博主',
    content: '虚空韩漫网的选品很有眼光，收录了很多高质量韩漫。作为评论者，这里的漫画库是我的重要参考。',
    rating: 5,
  },
];

const faqItems = [
  { q: '虚空韩漫网需要付费吗？', a: '完全免费！虚空韩漫网致力于为韩漫爱好者提供免费的高清阅读服务，所有漫画均可免费在线阅读，无需付费。' },
  { q: '漫画内容是否正版合规？', a: '是的，我们所有漫画内容均获得正版授权，严格遵守版权法规，确保每位读者都能安心阅读。' },
  { q: '如何找到想看的漫画？', a: '您可以通过首页的分类导航、搜索功能或直接浏览漫画列表来找到心仪的作品。分类涵盖恋爱、奇幻、动作、悬疑等多个品类。' },
  { q: '支持哪些设备阅读？', a: '虚空韩漫网完美适配PC、平板和手机等各类设备，采用响应式设计，无论使用哪种设备都能获得优秀的阅读体验。' },
];

export default function About() {
  const [activeTab, setActiveTab] = useState<'brand' | 'reputation' | 'contact'>('brand');

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于虚空韩漫网',
    description: '虚空韩漫网 - 免费韩漫在线阅读平台，提供高清下拉式韩漫全集。品牌介绍、用户口碑与联系方式。',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  const contactSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '虚空韩漫网',
    url: 'https://xukonghanman.com/',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@xukonghanman.com',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们_虚空韩漫网_免费韩漫在线阅读平台_品牌介绍"
        description="虚空韩漫网品牌介绍 - 了解我们的正版合规理念、用户口碑评价以及联系方式。作为免费韩漫在线阅读平台，致力于为韩漫爱好者提供优质的阅读体验。"
        keywords="虚空韩漫网, 关于我们, 韩漫平台, 免费韩漫, 品牌介绍, 用户口碑"
        canonicalUrl="https://xukonghanman.com/about"
        ogType="website"
        schema={{ ...aboutSchema, ...faqSchema, ...contactSchema }}
        breadcrumbItems={[
          { name: '首页', url: 'https://xukonghanman.com/' },
          { name: '关于我们', url: 'https://xukonghanman.com/about' },
        ]}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[{ name: '首页', url: '/' }, { name: '关于我们' }]} />

        <section className="relative w-full min-h-[320px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20minimalist%20reader%20friendly%20atmosphere%20with%20bookshelves%20and%20warm%20lighting%2C%20abstract%20literary%20aesthetic%2C%20soft%20cream%20and%20warm%20beige%20tones%2C%20cozy%20reading%20nook%20vibe%2C%20clean%20composition%2C%20simple%20elegant%20background%2C%20no%20text&width=1600&height=600&seq=about-hero-bg&orientation=landscape"
              alt="虚空韩漫网关于我们"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/40 via-foreground-900/20 to-foreground-900/50"></div>
          </div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-3xl md:text-5xl font-bold text-background-50 font-heading mb-3">
              关于虚空韩漫网
            </h1>
            <p className="text-sm md:text-base text-background-50/80 max-w-[500px] mx-auto">
              致力于为韩漫爱好者提供最优质的免费在线阅读体验
            </p>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background-50 to-transparent z-10"></div>
        </section>

        <section className="w-full max-w-[1200px] mx-auto px-4 md:px-6 -mt-8 relative z-20">
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-1 flex">
            {[
              { key: 'brand', label: '品牌介绍', icon: 'ri-building-line' },
              { key: 'reputation', label: '用户口碑', icon: 'ri-chat-heart-line' },
              { key: 'contact', label: '联系我们', icon: 'ri-mail-line' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as 'brand' | 'reputation' | 'contact')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                <i className={`${tab.icon} text-base`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </section>

        <section className="w-full max-w-[1200px] mx-auto px-4 md:px-6 py-8">
          {activeTab === 'brand' && (
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                {brandValues.map((v) => (
                  <div key={v.title} className="p-5 rounded-xl bg-background-50 border border-background-200/60 text-center">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 mx-auto mb-3">
                      <i className={`${v.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">{v.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
                <h2 className="text-xl font-bold text-foreground-900 font-heading mb-4">我们的故事</h2>
                <div className="text-sm text-foreground-600 leading-relaxed space-y-4">
                  <p>
                    虚空韩漫网诞生于一群韩漫爱好者的共同愿景。我们发现在国内寻找高质量、正版合规的韩漫阅读渠道并不容易，于是决定自己打造一个专注于韩国漫画的在线阅读平台。
                  </p>
                  <p>
                    从创立之初，我们就坚持三个原则：<strong className="text-foreground-800">正版合规、高清流畅、完全免费</strong>。我们相信，只有尊重创作者权益，才能让韩漫行业持续健康发展。每一部上线平台的作品都获得了正版授权。
                  </p>
                  <p>
                    我们的技术团队精心打造了下拉式阅读模式，让读者无需频繁翻页即可沉浸式浏览漫画。同时支持PC和移动端自适应显示，无论您使用什么设备，都能获得优秀的阅读体验。
                  </p>
                  <p>
                    经过持续努力，虚空韩漫网已经发展成为拥有<strong className="text-foreground-800">200+部正版韩漫、50万+活跃读者</strong>的优质阅读平台。我们将继续扩展漫画库，为读者带来更多精彩的韩漫作品。
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reputation' && (
            <div>
              <div className="text-center mb-8">
                <h2 className="text-xl font-bold text-foreground-900 font-heading mb-2">读者评价</h2>
                <p className="text-sm text-foreground-500">来自真实读者的反馈，是我们前进的动力</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {testimonials.map((t) => (
                  <div key={t.name} className="p-5 rounded-xl bg-background-50 border border-background-200/60">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${i < t.rating ? 'ri-star-fill text-secondary-500' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      &ldquo;{t.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-3 pt-3 border-t border-background-200">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 text-sm font-bold">
                        {t.name[0]}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                        <p className="text-xs text-foreground-500">{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 bg-primary-50/50 rounded-xl border border-primary-200/50 p-6 text-center">
                <p className="text-sm text-foreground-700 mb-1">
                  虚空韩漫网已获得 <strong className="text-primary-600">99.7%</strong> 的读者好评率
                </p>
                <p className="text-xs text-foreground-500">数据统计截止至 2026年8月</p>
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h2 className="text-xl font-bold text-foreground-900 font-heading mb-6">联系方式</h2>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-background-50 border border-background-200/60">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900 mb-0.5">电子邮箱</p>
                        <p className="text-sm text-foreground-500">contact@xukonghanman.com</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 rounded-xl bg-background-50 border border-background-200/60">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 shrink-0">
                        <i className="ri-customer-service-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900 mb-0.5">在线客服</p>
                        <p className="text-sm text-foreground-500">工作日 9:00 - 18:00</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 rounded-xl bg-background-50 border border-background-200/60">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900 mb-0.5">办公地址</p>
                        <p className="text-sm text-foreground-500">中国上海市浦东新区陆家嘴金融贸易区</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-background-50 border border-background-200/60">
                  <h2 className="text-lg font-bold text-foreground-900 font-heading mb-4">给我们留言</h2>
                  <form
                    data-readdy-form
                    className="flex flex-col gap-4"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
                      if (honeypot) return;
                      const formData = new FormData(form);
                      try {
                        const res = await fetch('https://readdy.ai/api/form/d9p1gdaftmtnfcfh0ft0', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                        });
                        const text = await res.text();
                        let parsed;
                        try { parsed = JSON.parse(text); } catch { parsed = null; }
                        if (res.ok && parsed?.code === 'OK') {
                          alert('留言已发送，我们会尽快回复！');
                          form.reset();
                        } else {
                          alert('发送失败，请稍后重试或直接发送邮件联系我们。');
                        }
                      } catch {
                        alert('网络错误，请稍后重试。');
                      }
                    }}
                  >
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        您的姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 text-foreground-900"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        电子邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 text-foreground-900"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        id="contact-subject"
                        type="text"
                        name="subject"
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 text-foreground-900"
                        placeholder="请输入留言主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 text-foreground-900 resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="visually-hidden"
                    />
                    <button
                      type="submit"
                      className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送留言
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <h2 className="text-xl font-bold text-foreground-900 font-heading text-center mb-8">
              常见问题
            </h2>
            <div className="flex flex-col gap-3">
              {faqItems.map((item, idx) => (
                <details key={idx} className="bg-background-50 rounded-lg border border-background-200/60 group">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-semibold text-foreground-900 list-none">
                    {item.q}
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}